import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { AnimationService } from '@educational-initiatives/angular-web-utilities';
import { TranslocoModule } from '@jsverse/transloco';
import gsap from 'gsap';
import type { MobileNavItem, NavItem } from '@app/shared';
import { LayoutIconComponent } from '@app/shared/components';
import {
  ANIMATION_DELAY,
  ANIMATION_DURATION,
  EASING,
} from '@shared/constants/animation.constants';

/**
 * @fileoverview MobileBottomNavComponent
 * @description
 * **The Mobile Navigation Bar**
 * A fixed bottom navigation bar for mobile. Only Home and Subscriptions
 * navigate; Manage and Add account are non-navigable buttons.
 * "More" opens the sidebar. GSAP animates a sliding hover pill between items.
 *
 * ARCHITECTURAL PRINCIPLES:
 * 1. Mobile-only navigation
 * 2. Fixed bottom with safe-area inset
 * 3. Selective navigation (navigable vs non-navigable items)
 * 4. Smooth hover indicator via GSAP (respects reduced motion)
 */
@Component({
  selector: 'app-mobile-bottom-nav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive, TranslocoModule, LayoutIconComponent],
  templateUrl: './mobile-bottom-nav.html',
  styleUrl: './mobile-bottom-nav.scss',
})
export class MobileBottomNavComponent {
  private readonly _animationService = inject(AnimationService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _hoverPillRef =
    viewChild<ElementRef<HTMLElement>>('hoverPill');

  private _gsapContext: gsap.Context | null = null;
  private _pillTween: gsap.core.Tween | null = null;
  private _isReducedMotionPreferred = false;

  /** Nav items to show (from MOBILE_NAV_ITEMS_RECORD) */
  public readonly navItems = input<NavItem[]>([]);

  /**
   * When false, the "More" entry that opens the sidebar is hidden.
   * Defaults to true for isolated tests; main layout binds from permissions.
   */
  public readonly sidebarEnabled = input(true);

  /** Emitted when "More" is clicked to open the sidebar */
  public readonly openSidebar = output<void>();

  constructor() {
    afterNextRender(() => this._initHoverAnimation());
  }

  protected _onOpenSidebar(): void {
    this.openSidebar.emit();
  }

  protected _isNavigable(item: NavItem): boolean {
    const mobileItem = item as MobileNavItem;
    return mobileItem.navigable !== false;
  }

  /** No-op for non-navigable items (placeholder for future modal/action) */
  protected _onNonNavigableClick(): void {
    // Placeholder: could open modal or emit event
  }

  /** Resolve full path for routerLink */
  protected _resolvePath(path: string): string {
    if (path.startsWith('/') || path.startsWith('http')) {
      return path;
    }
    return `/home/${path}`;
  }

  /** Animate hover pill to the target item's position */
  protected _onItemHover(event: Event): void {
    const target = event.currentTarget as HTMLElement;
    const pill = this._hoverPillRef()?.nativeElement;
    if (
      !pill ||
      !target ||
      this._isReducedMotionPreferred ||
      !this._gsapContext
    ) {
      return;
    }
    const rect = target.getBoundingClientRect();
    const navRect = target
      .closest('.mobile-bottom-nav')
      ?.getBoundingClientRect();
    if (!navRect) {
      return;
    }
    const x = rect.left - navRect.left;
    const width = rect.width;
    this._pillTween?.kill();
    this._pillTween = gsap.to(pill, {
      x,
      width,
      opacity: 1,
      duration: ANIMATION_DURATION.normal,
      delay: ANIMATION_DELAY.short,
      ease: EASING.smoothOut,
      overwrite: true,
    });
  }

  /** Hide pill on nav mouseleave (optional: could keep on last item) */
  protected _onNavMouseLeave(): void {
    const pill = this._hoverPillRef()?.nativeElement;
    if (pill && this._gsapContext && !this._isReducedMotionPreferred) {
      this._pillTween?.kill();
      gsap.to(pill, {
        opacity: 0,
        duration: ANIMATION_DURATION.fast,
        ease: EASING.smoothOut,
      });
    }
  }

  private _initHoverAnimation(): void {
    this._isReducedMotionPreferred =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this._isReducedMotionPreferred) {
      return;
    }
    this._gsapContext = this._animationService.createContext(this._destroyRef);
    const pill = this._hoverPillRef()?.nativeElement;
    if (pill) {
      gsap.set(pill, { opacity: 0 });
    }
  }
}
